/**
 * Pure, framework-free logic for the unified Help page (HELP_UNIFIED_ENABLED).
 *
 * Kept separate from the React view so the section-fetch mapping, search, and
 * old-route redirect logic can be unit-tested without a DOM. Nothing here reads
 * component state or the network — callers pass data in and get data out.
 *
 * DESIGN: design-handoff/concepts/help-elevated (01-help-fable) — the single
 * unified "How can we help?" page (Gordon 2026-07-14, supersedes the ISP-4012
 * 4-route split / PR #1773).
 */

import { HELP_CATEGORIES, ALL_STATIC_ARTICLES } from "../../data/helpContent";

/* ── Per-category visual theme (matches the deck's colored icon chips) ─────────
   accent = line-art icon stroke; chipBg = soft tint behind it. The FAQ section
   heading chip and the category card chip share the SAME entry so the card grid
   doubles as a legend (deck note). `icon` names a line-art glyph in the view. */
export const CATEGORY_THEME = {
  "getting-started": { accent: "#8F38EF", chipBg: "#F1E9FE", icon: "rocket" },
  "building-wheels": { accent: "#0B7285", chipBg: "#E6F7F9", icon: "palette" },
  "sharing-cloning": { accent: "#C42B8A", chipBg: "#FCEBF5", icon: "share" },
  "account-billing": { accent: "#087F5B", chipBg: "#E6F6F1", icon: "card" },
  "troubleshooting": { accent: "#B45309", chipBg: "#FDF3E3", icon: "wrench" },
};

/** Theme for the always-present "Message support" card (routes to contact). */
export const SUPPORT_CARD_THEME = { accent: "#0B6BC2", chipBg: "#E8F1FB", icon: "mail" };

/** Fallback theme for any backend category id not in the static map. */
export const DEFAULT_THEME = { accent: "#8F38EF", chipBg: "#F1E9FE", icon: "help" };

export const getCategoryTheme = (id) => CATEGORY_THEME[id] || DEFAULT_THEME;

/** Brand ink (deck navy) for on-white headings on this surface. */
export const HELP_INK = "#123051";

/* ── Scroll-target ids used for deep-linking into page sections ──────────────
   Old routes redirect to /help?section=<key>; the view scrolls to these ids. */
export const SECTION_IDS = {
  faq: "help-faq",
  support: "help-contact",
};

export const sectionScrollId = (sectionKey) => SECTION_IDS[sectionKey] || null;

/* ── Old-route -> unified-page-section redirect map ──────────────────────────
   When HELP_UNIFIED_ENABLED is ON, the previously-separate routes collapse into
   the one page but keep their deep-link intent by redirecting into a section.
   Keyed by the concrete pathname so routes.js and the tests share one truth. */
export const HELP_UNIFIED_REDIRECTS = {
  "/faq": "/help?section=faq",
  "/support": "/help?section=support",
  "/contact": "/help?section=support",
};

/**
 * Resolve the redirect target for an old help route.
 * @param {string} pathname  e.g. "/faq"
 * @param {boolean} enabled  HELP_UNIFIED_ENABLED
 * @returns {string|null} the "/help?section=..." target, or null when the flag
 *   is OFF (no redirect — the old route renders normally) or the path is not a
 *   redirected old route.
 */
export function resolveHelpRedirect(pathname, enabled) {
  if (!enabled) return null;
  return HELP_UNIFIED_REDIRECTS[pathname] || null;
}

/**
 * Normalise the backend FAQ payload (GET modules/faq/ — a FLAT list of
 * { id, question, answer }) into the page's category-section shape so it renders
 * through the same UI as the static content. Rows that already carry a category
 * are grouped by it; everything else falls under "General". Returns null when the
 * backend has nothing usable (caller then falls back to the static categories).
 *
 * This IS the "section fetch mapping": one flat endpoint response -> the page's
 * sections. (Reuses the mapping PR #1773 introduced for its per-route fetches.)
 */
export function normaliseBackendFaq(payload) {
  const rows = payload?.data?.data || payload?.data || [];
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const byCategory = new Map();
  rows.forEach((row, idx) => {
    const catTitle =
      row.category || row.category_name || row.section || "General";
    const catId = String(catTitle)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const key = catId || "general";
    if (!byCategory.has(key)) {
      byCategory.set(key, { id: key, title: catTitle, articles: [] });
    }
    byCategory.get(key).articles.push({
      id: row.id != null ? `faq-${row.id}` : `faq-idx-${idx}`,
      question: row.question,
      answer: row.answer,
      keywords: row.keywords || [],
      media: null,
      docHref: row.doc_href || null,
      placeholder: false,
    });
  });

  return Array.from(byCategory.values());
}

/**
 * Resolve the content source: live backend sections when usable, else the
 * static fallback. Returns { categories, usingStatic }.
 */
export function resolveCategories(payload, staticCategories = HELP_CATEGORIES) {
  const backend = normaliseBackendFaq(payload);
  if (backend && backend.length) return { categories: backend, usingStatic: false };
  return { categories: staticCategories, usingStatic: true };
}

/** Lower-cased searchable haystack for one article. */
function haystackFor(article, categoryTitle) {
  return [
    article.question,
    article.answer,
    ...(article.keywords || []),
    categoryTitle || article.categoryTitle,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/**
 * Build a flat, searchable index from category sections. Each entry keeps its
 * category id/title for result grouping + labelling.
 */
export function buildSearchIndex(categories) {
  return categories.flatMap((cat) =>
    cat.articles.map((a) => ({
      ...a,
      categoryId: cat.id,
      categoryTitle: cat.title,
      _haystack: haystackFor(a, cat.title),
    }))
  );
}

/** Search index built from the pre-flattened static article list. */
export function buildStaticSearchIndex(articles = ALL_STATIC_ARTICLES) {
  return articles.map((a) => ({ ...a, _haystack: haystackFor(a) }));
}

/**
 * Filter a search index by a raw query string (substring match on the haystack).
 * Empty / whitespace query -> [] (the caller shows the browse view instead).
 */
export function searchArticles(index, rawQuery) {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) return [];
  return index.filter((a) => a._haystack.includes(q));
}

/**
 * Build the trimmed support-request payload sent to POST /api/v1/support/contact/.
 * Kept pure so the mapping is testable without mounting the form.
 */
export function buildSupportPayload(values, recaptchaToken) {
  const payload = {
    name: (values.name || "").trim(),
    email: (values.email || "").trim(),
    subject: (values.subject || "").trim(),
    message: (values.message || "").trim(),
  };
  if (recaptchaToken) payload.recaptcha_token = recaptchaToken;
  return payload;
}
